import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, ShieldCheck, Lock, Database, Server, KeyRound, Eye,
  FileCheck2, RefreshCw, BadgeCheck, FileText, Cloud, Network, Landmark, Globe,
  Search, MapPinned, Rocket, ClipboardCheck,
} from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal, SectionHeading } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { RegionMap } from "../components/trust/RegionMap";

const AREAS = [
  { icon: Lock, t: "Security", d: "Encryption in transit and at rest, hardened infrastructure and least-privilege access. Detailed controls available under NDA." },
  { icon: ShieldCheck, t: "AI governance", d: "Operated under an ISO 42001 AI management system: versioned policies, monitoring and continual improvement." },
  { icon: Database, t: "Data handling", d: "Configurable inspection, redaction and retention. Sensitive data can be minimised; only a minimal evidence reference retained where appropriate." },
  { icon: Server, t: "Deployment", d: "Hosted, private cloud, in-country sovereign, on-premise or fully air-gapped — matched to your sensitivity and data-residency requirements." },
  { icon: KeyRound, t: "Access controls", d: "Role-based access, authentication and administrative separation of duties." },
  { icon: Eye, t: "Human oversight", d: "Escalation routes and named reviewers keep meaningful human control over decisions." },
  { icon: FileCheck2, t: "Auditability", d: "Linked, time-stamped records of events, policies, actions and decisions for internal review." },
  { icon: RefreshCw, t: "Business continuity", d: "Backup, recovery and resilience practices. Specifics available on request." },
];

const DEPLOYMENTS = [
  { icon: Cloud, t: "norse3-hosted (UK)", d: "Managed SaaS in UK data centres — the fastest route to a controlled environment.", tag: "United Kingdom" },
  { icon: Cloud, t: "norse3-hosted (EU)", d: "Managed SaaS with European data residency for EU operations.", tag: "European Union" },
  { icon: Network, t: "Your private cloud", d: "Deployed into your own AWS, Azure or GCP account, in the cloud region you choose.", tag: "Region of your choice" },
  { icon: Landmark, t: "In-country / sovereign", d: "A dedicated deployment on infrastructure inside your own nation, so data and processing stay fully onshore.", tag: "UAE · KSA / GCC · UK · EU" },
  { icon: Server, t: "On-premise", d: "Runs entirely in your own data centre, fully under your operational control.", tag: "Your infrastructure" },
  { icon: Lock, t: "Air-gapped", d: "A disconnected deployment for the most sensitive defence and national-security workloads.", tag: "Isolated network" },
];

const RESIDENCY_PROMISES = [
  "Your data never leaves the jurisdiction you choose.",
  "Processing and storage stay within your national borders — or on infrastructure you control.",
  "In customer-controlled and sovereign deployments, no production data is returned to norse3 and we hold no access to your live content.",
  "Local data-residency and regulatory requirements supported, confirmed during technical discovery.",
];

const SOVEREIGN_STEPS = [
  { icon: Search, t: "Discovery", d: "We map your data-residency, security and regulatory requirements — which jurisdiction, which controls, who needs access." },
  { icon: MapPinned, t: "In-country design", d: "We agree the hosting location and architecture inside your chosen nation, so data and processing stay fully onshore." },
  { icon: Rocket, t: "Deploy", d: "norse3 is stood up in your sovereign environment — dedicated, private, on-premise or air-gapped as required." },
  { icon: ClipboardCheck, t: "Assure & operate", d: "Controls are validated, evidence starts flowing, and your team retains oversight with no production data returned to norse3." },
];

const CERTS = [
  { name: "ISO 9001", status: "Certified", tone: "green" },
  { name: "ISO 42001", status: "Certified", tone: "green" },
  { name: "SOC 2", status: "In progress / roadmap", tone: "amber" },
];

const DOCS = [
  "Security overview (available under NDA)",
  "Data processing information",
  "Sub-processor list",
  "Deployment & architecture summary",
];

const FAQ = [
  ["Where is data processed and stored?", "This depends on your chosen deployment. norse3 can run in the UK, the EU, in your own cloud region, on-premise, or as a dedicated in-country / sovereign deployment — so data and processing stay within the jurisdiction you choose. The specifics are confirmed during technical discovery."],
  ["Can norse3 run entirely inside our own country?", "Yes. Our in-country / sovereign model deploys norse3 onto infrastructure within your own nation — for example the United Arab Emirates or Saudi Arabia / GCC, as well as the UK and EU. Data and processing stay fully onshore and never leave the jurisdiction, supporting national data-residency requirements."],
  ["Can we run norse3 on our own servers, or air-gapped?", "Yes. norse3 supports on-premise deployment in your own data centre and fully air-gapped, disconnected environments for the most sensitive defence and national-security workloads."],
  ["Do you retain the content of interactions?", "Retention is configurable. norse3 can operate on a minimal-evidence basis, retaining a reference rather than raw sensitive content. In customer-controlled and sovereign deployments, no production data is returned to norse3."],
  ["Is SOC 2 certified?", "SOC 2 is a roadmap / readiness item and is not presented as an achieved certification. ISO 9001 and ISO 42001 are certified."],
  ["Can we review your security documentation?", "Yes — security and data-processing documentation is available to qualified prospects under NDA."],
];

export default function TrustCentre() {
  useEffect(() => { document.title = "Trust Centre | norse3"; }, []);
  return (
    <div>
      <PageHero
        eyebrow="Trust Centre"
        title="Built for regulated enterprise."
        subtitle="How norse3 approaches security, AI governance, data handling and oversight. Certified where stated; where details require verification they are provided under NDA rather than asserted here."
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Request a technical assessment <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
        </div>
      </PageHero>

      {/* Certifications */}
      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Certifications" title="Independently certified management systems." /></Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {CERTS.map((c) => (
              <div key={c.name} data-testid={`trust-cert-${c.name.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-navy-900/60 px-5 py-4">
                <BadgeCheck className={`h-6 w-6 ${c.tone === "green" ? "text-signal-green" : "text-signal-amber"}`} />
                <div>
                  <p className="text-sm font-semibold text-foreground">{c.name}</p>
                  <p className={`text-xs ${c.tone === "green" ? "text-signal-green" : "text-signal-amber"}`}>{c.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="border-t border-white/10 py-14 lg:py-16 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="How we operate" title="Security, governance and data handling." /></Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AREAS.map((a, i) => { const Icon = a.icon; return (
              <Reveal key={a.t} delay={i * 40}><div className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-base font-semibold text-foreground">{a.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.d}</p>
              </div></Reveal>
            ); })}
          </div>
        </div>
      </section>

      {/* Deployment & data residency */}
      <section className="border-t border-white/10 py-14 lg:py-16" data-testid="trust-deployment-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Deployment & data residency"
              title="Run norse3 wherever your data has to live."
              subtitle="Governance is only useful if it fits your rules. norse3 deploys from managed UK/EU SaaS all the way to sovereign, on-premise and air-gapped environments — so you keep control of where every interaction is processed and stored."
            />
          </Reveal>

          {/* Sovereign / in-country highlight */}
          <Reveal delay={60}>
            <div className="mt-8 rounded-lg border border-primary/25 bg-primary/[0.06] p-6 lg:p-8" data-testid="trust-sovereign-callout">
              <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 border-l-2 border-primary pl-2.5 py-0.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-primary rounded-none cursor-default select-none">
                    <Globe className="h-3.5 w-3.5" /> Sovereign & in-country
                  </span>
                  <h3 className="mt-4 text-xl sm:text-2xl font-bold text-foreground">Your data never leaves your chosen jurisdiction.</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Where a country requires systems to run on infrastructure inside its own borders, norse3 can be
                    deployed as a dedicated in-country instance — data and processing stay fully onshore. It is a
                    well-understood deployment we can put in place as part of your programme.
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {RESIDENCY_PROMISES.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-foreground"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{p}</span></li>
                    ))}
                  </ul>
                  <Button asChild className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="trust-sovereign-cta">
                    <Link to="/book-a-demo" state={{ enquiry: "In-country / sovereign deployment", control_area: "In-country / sovereign deployment", message: "I'd like to discuss an in-country / sovereign deployment of norse3 (data must stay within a specific jurisdiction). Please route this to the right person." }}>
                      Discuss in-country deployment <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <RegionMap />
              </div>
            </div>
          </Reveal>

          {/* Sovereign deployment in 4 steps */}
          <Reveal delay={80}>
            <div className="mt-6 rounded-lg border border-white/10 bg-navy-900/60 p-6 lg:p-8" data-testid="trust-sovereign-steps">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/12 text-primary"><Landmark className="h-4 w-4" /></span>
                <h3 className="text-lg font-semibold text-foreground">Sovereign deployment in 4 steps</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">A well-understood path we run as part of your programme — typically weeks, not years.</p>
              <ol className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
                {SOVEREIGN_STEPS.map((s, i) => { const Icon = s.icon; return (
                  <li key={s.t} className="relative" data-testid={`sovereign-step-${i}`}>
                    <div className="hidden lg:block absolute top-5 left-[calc(50%+1.75rem)] right-0 h-px bg-white/10" aria-hidden="true" style={{ display: i === SOVEREIGN_STEPS.length - 1 ? "none" : undefined }} />
                    <div className="relative flex items-center gap-3">
                      <span className="relative z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                      <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-primary">Step {i + 1}</span>
                    </div>
                    <h4 className="mt-3 text-base font-semibold text-foreground">{s.t}</h4>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                  </li>
                ); })}
              </ol>
            </div>
          </Reveal>

          {/* Deployment models */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {DEPLOYMENTS.map((m, i) => { const Icon = m.icon; return (
              <Reveal key={m.t} delay={i * 60}><div className="card-rise h-full rounded-xl border border-white/10 bg-navy-900/60 p-6 hover:border-primary/40" data-testid={`trust-deployment-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                  <span className="rounded-md border border-white/12 bg-white/[0.03] px-2.5 py-1 text-[0.7rem] font-medium text-muted-foreground">{m.tag}</span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground">{m.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.d}</p>
              </div></Reveal>
            ); })}
          </div>
          <p className="mt-6 text-xs text-muted-foreground/70">norse3 is not a regulator or compliance adviser, and deployment options are confirmed against your specific data-residency and security requirements during technical discovery.</p>
        </div>
      </section>

      {/* Sample evidence record */}
      <section className="border-t border-white/10 py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <SectionHeading eyebrow="Evidence" title="What a real evidence record looks like." subtitle="Every intervention produces a linked, time-stamped record. Below is an anonymised, illustrative example — no real customer data." />
              <ul className="mt-6 space-y-2.5">
                {["Reconstruct exactly what happened, and when", "Show the policy that applied and the action taken", "Record the human reviewer and their decision", "Retain a reference — not the raw sensitive content"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-foreground"><FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{t}</span></li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-lg border border-white/10 bg-navy-900/70 overflow-hidden" data-testid="evidence-record-panel">
                <div className="flex items-center justify-between border-b border-white/10 bg-navy-800/60 px-5 py-3">
                  <div className="flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-primary" /><p className="text-sm font-semibold text-foreground">Evidence record</p></div>
                  <span className="rounded-md border border-signal-green/40 bg-signal-green/10 px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-signal-green">Sealed</span>
                </div>
                <dl className="divide-y divide-white/8">
                  {[
                    ["Reference", "EVD-2F41-9C07"],
                    ["Timestamp (UTC)", "2026-05-14 09:42:17"],
                    ["Channel", "Employee copilot"],
                    ["Risk classification", "Personal data · identity document"],
                    ["Policy applied", "Restricted identity information v3"],
                    ["Action taken", "Blocked before submission"],
                    ["Reviewer", "Reviewer #114 (Compliance)"],
                    ["Decision", "Upheld — no action required"],
                    ["Content retained", "None · reference only"],
                    ["Integrity hash", "sha256:9f2a…c1de"],
                  ].map(([k, v], i) => (
                    <div key={k} className="flex items-start justify-between gap-4 px-5 py-2.5">
                      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
                      <dd className={`text-sm text-right ${i === 5 ? "text-signal-red font-medium" : "text-foreground"} ${k === "Reference" || k === "Integrity hash" ? "font-mono text-xs" : ""}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="px-5 py-3 text-[0.7rem] text-muted-foreground/70 border-t border-white/10">Anonymised, illustrative record. Synthetic data only.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Docs + FAQ */}
      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <SectionHeading eyebrow="Documentation" title="Security documentation." subtitle="Available to qualified prospects under NDA." />
            <ul className="mt-6 space-y-2.5">
              {DOCS.map((d) => (
                <li key={d} className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-900/60 p-4">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-sm text-foreground">{d}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <SectionHeading eyebrow="FAQ" title="Trust & assurance questions." />
            <div className="mt-6 space-y-3">
              {FAQ.map(([q, a]) => (
                <details key={q} className="group rounded-xl border border-white/10 bg-navy-900/60 p-5">
                  <summary className="cursor-pointer list-none text-sm font-semibold text-foreground flex items-center justify-between">{q}<ArrowDown className="h-4 w-4 text-primary transition-transform group-open:rotate-180" /></summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
                </details>
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-primary/25 bg-primary/[0.06] p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <p className="text-sm text-foreground">Need data to stay in a specific country?</p>
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0" data-testid="trust-faq-sovereign-cta">
                <Link to="/book-a-demo" state={{ enquiry: "In-country / sovereign deployment", control_area: "In-country / sovereign deployment", message: "I'd like to discuss an in-country / sovereign deployment of norse3 (data must stay within a specific jurisdiction). Please route this to the right person." }}>
                  Discuss in-country deployment <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Need something specific for your review?</h2>
          <p className="mt-3 text-muted-foreground">Ask us about deployment, data handling or documentation for your environment.</p>
          <Button asChild className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Discuss your environment <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
        </div>
      </section>
    </div>
  );
}
